package com.esoris.esocomponent.ui.common

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.*
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.*
import compose.icons.FeatherIcons
import compose.icons.feathericons.Clipboard
import compose.icons.feathericons.X

import kotlinx.coroutines.launch

@Composable
fun EsoTextField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
    label: String? = null,
    placeholder: String? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
    singleLine: Boolean = true,
    maxLines: Int = 1,
    textAlign: TextAlign = TextAlign.Start,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    errorMessage: String? = null,
    visualTransformation: VisualTransformation =
        VisualTransformation.None,
    shape: CornerBasedShape =
        RoundedCornerShape(8.dp),
    colors: TextFieldColors =
        TextFieldDefaults.colors(),
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default,
    showPasteButton: Boolean = false,
) {

    var isFocused by remember {
        mutableStateOf(false)
    }

    val clipboard = LocalClipboard.current

    val scope = rememberCoroutineScope()

    val errorColor =
        MaterialTheme.colorScheme.error

    val textFieldColors = colors.copy(
        focusedIndicatorColor =
            if (errorMessage != null)
                errorColor
            else
                colors.focusedIndicatorColor,

        unfocusedIndicatorColor =
            Color.Transparent,
    )

    Column(
        modifier = Modifier.wrapContentHeight()
    ) {

        TextField(
            value = value,
            onValueChange = onValueChange,

            textStyle = LocalTextStyle.current.copy(
                textAlign = textAlign
            ),

            modifier = modifier
                .fillMaxWidth()
                .onFocusChanged {
                    isFocused = it.isFocused
                },

            label = label?.let {
                { Text(it) }
            },

            placeholder = placeholder?.let {
                {
                    Text(
                        text = it,
                        style =
                            MaterialTheme
                                .typography
                                .bodySmall,

                        textAlign = textAlign,

                        modifier = Modifier.alpha(0.7f)
                    )
                }
            },

            leadingIcon = leadingIcon,

            trailingIcon =
                if (showPasteButton ||
                    trailingIcon != null
                ) {

                    {
                        Row {

                            if (showPasteButton) {

                                IconButton(
                                    onClick = {

                                        scope.launch {

                                            val text =
                                                clipboard
                                                    .getClipEntry()
                                                    ?.toString()

                                            if (
                                                !text.isNullOrBlank()
                                            ) {
                                                onValueChange(text)
                                            }
                                        }
                                    }
                                ) {

                                    Icon(
                                        imageVector =
                                            FeatherIcons.Clipboard,

                                        contentDescription =
                                            "Paste"
                                    )
                                }
                            }

                            trailingIcon?.invoke()
                        }
                    }

                } else null,

            singleLine = singleLine,
            maxLines = maxLines,
            enabled = enabled,
            readOnly = readOnly,
            isError = errorMessage != null,
            visualTransformation =
                visualTransformation,
            shape = shape,
            colors = textFieldColors,
            keyboardOptions =
                keyboardOptions,
        )

        if (errorMessage != null) {
            EsoTextError(errorMessage)
        }
    }
}

/* --------------------------------------------------------------
 * TEXTFIELDVALUE VERSION
 * -------------------------------------------------------------- */

@Composable
fun EsoTextField(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
    label: String? = null,
    placeholder: String? = null,
    leadingIcon: @Composable (() -> Unit)? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
    singleLine: Boolean = true,
    maxLines: Int = 1,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    errorMessage: String? = null,
    colors: TextFieldColors =
        TextFieldDefaults.colors(),
    visualTransformation: VisualTransformation =
        VisualTransformation.None,
    shape: CornerBasedShape =
        RoundedCornerShape(8.dp),
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default,
) {

    var isFocused by remember {
        mutableStateOf(false)
    }

    val errorColor =
        MaterialTheme.colorScheme.error

    val textFieldColors = colors.copy(
        focusedIndicatorColor =
            if (errorMessage != null)
                errorColor
            else
                colors.focusedIndicatorColor,

        unfocusedIndicatorColor =
            Color.Transparent,
    )

    Column {

        TextField(
            value = value,
            onValueChange = onValueChange,

            modifier = modifier
                .onFocusChanged {
                    isFocused = it.isFocused
                },

            label = label?.let {
                { Text(it) }
            },

            placeholder = placeholder?.let {
                {
                    Text(
                        text = it,
                        style =
                            MaterialTheme
                                .typography
                                .bodySmall,

                        modifier =
                            Modifier.alpha(0.7f)
                    )
                }
            },

            leadingIcon = leadingIcon,
            trailingIcon = trailingIcon,

            singleLine = singleLine,
            maxLines = maxLines,

            enabled = enabled,
            readOnly = readOnly,

            isError = errorMessage != null,

            visualTransformation =
                visualTransformation,

            colors = textFieldColors,

            shape = shape,

            keyboardOptions =
                keyboardOptions,
        )

        if (errorMessage != null) {
            EsoTextError(errorMessage)
        }
    }
}

/* --------------------------------------------------------------
 * PRICE FIELD
 * -------------------------------------------------------------- */

@Composable
fun EsoTextFieldPrice(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
    label: String = "Price",
    placeholder: String = "",
    currencySymbol: String = "Rp",
    showCurrency: Boolean = false,
    errorMessage: String? = null,
    enabled: Boolean = true,
) {

    var textFieldValue by remember {

        mutableStateOf(
            TextFieldValue(
                formatCurrency(value)
            )
        )
    }

    LaunchedEffect(value) {

        val formatted =
            formatCurrency(value)

        if (formatted != textFieldValue.text) {

            textFieldValue =
                TextFieldValue(
                    text = formatted,
                    selection =
                        TextRange(formatted.length)
                )
        }
    }

    EsoTextField(
        value = textFieldValue,

        onValueChange = { newValue ->

            val newRawText =
                newValue.text

            val selIndex =
                newValue.selection.start
                    .coerceIn(
                        0,
                        newRawText.length
                    )

            val digitsBeforeCursor =
                newRawText
                    .substring(0, selIndex)
                    .count {
                        it.isDigit()
                    }

            val digitsOnly =
                newRawText.filter {
                    it.isDigit()
                }

            val formatted =
                formatCurrency(digitsOnly)

            val newCursor =
                mapDigitsCountToFormattedIndex(
                    formatted,
                    digitsBeforeCursor
                )

            textFieldValue =
                TextFieldValue(
                    text = formatted,
                    selection =
                        TextRange(
                            newCursor.coerceIn(
                                0,
                                formatted.length
                            )
                        )
                )

            onValueChange(digitsOnly)
        },

        label = label,
        placeholder = placeholder,

        leadingIcon =
            if (showCurrency) {

                {
                    EsoText(
                        text = currencySymbol,
                        color =
                            MaterialTheme
                                .colorScheme
                                .onSurfaceVariant
                    )
                }

            } else null,

        errorMessage = errorMessage,
        enabled = enabled,

        singleLine = true,
        maxLines = 1,

        readOnly = !enabled,

        visualTransformation =
            VisualTransformation.None,

        keyboardOptions =
            KeyboardOptions(
                keyboardType =
                    KeyboardType.Number
            )
    )
}

/* --------------------------------------------------------------
 * SEARCH FIELD
 * -------------------------------------------------------------- */

@Composable
fun EsoTextFieldSearch(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier.fillMaxWidth(),
    placeholder: String? = "Search...",
    leadingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    maxLines: Int = 1,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default,
    shape: CornerBasedShape =
        RoundedCornerShape(8.dp),
    height: Dp = 48.dp
) {

    val trailingIcon:
            (@Composable (() -> Unit))? =

        if (value.isNotEmpty()) {

            {
                IconButton(
                    onClick = {
                        onValueChange("")
                    }
                ) {

                    Icon(
                        imageVector =
                            FeatherIcons.X,

                        contentDescription =
                            "Clear text",

                        modifier =
                            Modifier.alpha(0.7f)
                    )
                }
            }

        } else null

    val textStyle = when {

        height <= 48.dp ->
            MaterialTheme.typography.bodySmall

        height <= 56.dp ->
            MaterialTheme.typography.bodyMedium

        else ->
            MaterialTheme.typography.bodyLarge
    }

    TextField(
        value = value,

        onValueChange = onValueChange,

        modifier =
            modifier.height(height),

        placeholder = {

            if (placeholder != null) {

                Text(
                    text = placeholder,
                    style = textStyle
                )
            }
        },

        leadingIcon = leadingIcon,
        trailingIcon = trailingIcon,

        singleLine = true,
        maxLines = maxLines,

        enabled = enabled,
        readOnly = readOnly,

        shape = shape,

        colors =
            TextFieldDefaults.colors(
                focusedIndicatorColor =
                    Color.Transparent,

                unfocusedIndicatorColor =
                    Color.Transparent,

                disabledIndicatorColor =
                    Color.Transparent
            ),

        keyboardOptions =
            keyboardOptions,

        textStyle = textStyle
    )
}

/* --------------------------------------------------------------
 * SEARCH FIELD TEXTFIELDVALUE
 * -------------------------------------------------------------- */

@Composable
fun EsoTextFieldSearch(
    value: TextFieldValue,
    onValueChange: (TextFieldValue) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String? = "Search...",
    leadingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    readOnly: Boolean = false,
    maxLines: Int = 1,
    keyboardOptions: KeyboardOptions =
        KeyboardOptions.Default,
    shape: CornerBasedShape =
        RoundedCornerShape(8.dp),
    height: Dp = 48.dp
) {

    EsoTextFieldSearch(
        value = value.text,

        onValueChange = { newText ->

            onValueChange(
                value.copy(
                    text = newText,
                    selection =
                        TextRange(newText.length)
                )
            )
        },

        modifier = modifier,
        placeholder = placeholder,
        leadingIcon = leadingIcon,
        enabled = enabled,
        readOnly = readOnly,
        maxLines = maxLines,
        keyboardOptions =
            keyboardOptions,
        shape = shape,
        height = height
    )
}

/* --------------------------------------------------------------
 * HELPERS
 * -------------------------------------------------------------- */

private fun mapDigitsCountToFormattedIndex(
    formatted: String,
    digitsBeforeCursor: Int
): Int {

    if (digitsBeforeCursor <= 0) {
        return 0
    }

    var digitsSeen = 0

    formatted.forEachIndexed { idx, ch ->

        if (ch.isDigit()) {
            digitsSeen++
        }

        if (digitsSeen == digitsBeforeCursor) {
            return idx + 1
        }
    }

    return formatted.length
}

private fun formatCurrency(
    value: String
): String {

    if (value.isEmpty()) {
        return ""
    }

    return value
        .reversed()
        .chunked(3)
        .joinToString(".")
        .reversed()
}